import { useEffect, useState } from 'react';
import { Camera, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '@utils/api';

function normalizePhotoSrc(url, siteOrigin) {
  const u = (url || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('/')) {
    const base = (siteOrigin || '').replace(/\/$/, '');
    return base ? `${base}${u}` : u;
  }
  return u;
}

/**
 * Multi-photo upload dialog with participant-share choice.
 */
const UploadPhotosDialog = ({
  open,
  onClose,
  activityId,
  onUploaded,
  initialFiles = [],
}) => {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [shareWithEveryone, setShareWithEveryone] = useState(null);
  const [yaarviPromo, setYaarviPromo] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && initialFiles?.length) {
      setFiles(Array.from(initialFiles));
    }
  }, [open, initialFiles]);

  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!files.length) {
      toast.error('Choose at least one photo');
      return;
    }
    if (shareWithEveryone !== true && shareWithEveryone !== false) {
      toast.error('Choose whether to share with everyone');
      return;
    }

    try {
      setSubmitting(true);
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('activityId', activityId);
        const { data } = await api.post('/aws/upload-activity-memory', fd);
        const fileUrl = (data.fileUrl || '').trim();
        if (!fileUrl) throw new Error('Upload failed');
        urls.push(fileUrl);
      }

      await api.post(`/memories/activities/${activityId}/photos`, {
        urls,
        shareWithParticipants: shareWithEveryone,
        shareScope: yaarviPromo ? 'yaarvi_promo' : 'private_only',
      });

      toast.success(
        shareWithEveryone
          ? 'Photos uploaded. Participants can accept them onto their profiles.'
          : 'Photos saved to your memories.',
      );
      onUploaded?.();
      onClose?.();
      setFiles([]);
      setShareWithEveryone(null);
      setYaarviPromo(false);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not upload');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-end justify-center sm:items-center">
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="memory-card relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl p-5 shadow-2xl sm:rounded-3xl sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-surface-900 dark:text-white">Upload Photos</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="flex cursor-pointer flex-col items-center gap-2 rounded-2xl border border-dashed border-brand-300/60 bg-brand-50/40 px-4 py-6 dark:border-brand-500/30 dark:bg-brand-950/20">
            <Camera size={28} className="text-brand-500" />
            <span className="text-sm font-medium text-surface-700 dark:text-surface-200">
              {files.length ? `${files.length} photo${files.length === 1 ? '' : 's'} selected` : 'Tap to choose photos'}
            </span>
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(ev) => {
                const next = Array.from(ev.target.files || []).slice(0, 20);
                setFiles(next);
              }}
            />
          </label>

          {previews.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {previews.map((src) => (
                <img
                  key={src}
                  src={src}
                  alt=""
                  className="aspect-square w-full rounded-xl object-cover"
                />
              ))}
            </div>
          )}

          {files.length > 0 && (
            <fieldset className="space-y-3 rounded-2xl bg-surface-50/80 p-4 dark:bg-surface-900/50">
              <legend className="px-1 text-sm font-semibold text-surface-800 dark:text-surface-100">
                Share these photos with everyone who joined this activity?
              </legend>
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="radio"
                  name="shareEveryone"
                  className="mt-1"
                  checked={shareWithEveryone === true}
                  onChange={() => setShareWithEveryone(true)}
                />
                <span>
                  <span className="font-medium text-surface-900 dark:text-white">Yes</span>
                  {shareWithEveryone === true && (
                    <span className="mt-1 block text-xs text-surface-500 dark:text-surface-400">
                      These photos will be available for every participant if they accept.
                    </span>
                  )}
                </span>
              </label>
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="radio"
                  name="shareEveryone"
                  className="mt-1"
                  checked={shareWithEveryone === false}
                  onChange={() => setShareWithEveryone(false)}
                />
                <span className="font-medium text-surface-900 dark:text-white">No</span>
              </label>

              <label className="flex cursor-pointer items-start gap-2 border-t border-surface-200/60 pt-3 dark:border-surface-700/50">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={yaarviPromo}
                  onChange={(e) => setYaarviPromo(e.target.checked)}
                />
                <span className="text-sm text-surface-600 dark:text-surface-300">
                  Also allow Yaarvi to feature these on Instagram and our pages
                </span>
              </label>
            </fieldset>
          )}

          <button
            type="submit"
            disabled={submitting || !files.length}
            className="btn-primary w-full !py-3"
          >
            {submitting ? 'Uploading…' : 'Upload'}
          </button>
        </form>
      </div>
    </div>
  );
};

export { normalizePhotoSrc };
export default UploadPhotosDialog;
